import React, { useEffect, useState } from 'react';
import { ChevronRightIcon, TruckIcon, ShieldCheckIcon } from 'lucide-react';
import { VIEW_SIZING_CHART_EVENT } from './PurchaseCard';
export type TabId = 'description' | 'sizing' | 'related' | 'shipping';
const RELATED = [{
  name: 'Daedo Shin Guard',
  image: 'https://drwzpk38qkpfb.cloudfront.net/core/getthumb?site=9848&user=1373&w=400&h=400&file=uploaded%2Fimages%2FWacoku%2FWTF%20B167.jpg800x800.jpg',
  href: '/shop/wacoku-products/'
}, {
  name: 'Daedo Forearm Guard',
  image: 'https://drwzpk38qkpfb.cloudfront.net/core/getthumb?site=9848&user=1373&w=400&h=400&file=uploaded%2Fimages%2FWacoku%2FWTF%20B166.jpg800x800.jpg',
  href: '/shop/wacoku-products/'
}, {
  name: 'Daedo WT/WTF TKD Gloves',
  image: 'https://drwzpk38qkpfb.cloudfront.net/core/getthumb?site=9848&user=1373&w=400&h=400&file=uploaded%2Fimages%2FWacoku%2FWTF%20B124W.jpg800x800.jpg',
  href: '/shop/wacoku-products/'
}];
const SIZING_TOP = {
  header: ['mm (KOR)', '195', '200', '205', '210', '215', '220', '225', '230', '235', '240', '245'],
  rows: [['ZA 2020', '26', '27', '28', '29', '30', '31', '32', '33', '34', '35', '36'], ['ZA 2021', '26', '27', '28', '29', '30', '31', '32', '33', '34', '35', '36'], ['ZA 2024', '', '', '', '', '', '', '', '', '35', '36', '37'], ['ZA 3010', '', '', '', '', '', '', '', '', '35', '36', '37'], ['ZA 3020', '', '', '', '', '', '', '', '', '35', '36', '37'], ['PRO 15944', '', '', '', '', '', 'XXS (35)', 'XXS (35)', 'XXS (36-37)', 'XXS (36-37)', 'S (38)', 'S (38)'], ['PRITF 2022', '', '', '', '', '', '', '', 'XXS', 'XXS', 'XS', 'XS']]
};
const SIZING_BOTTOM = {
  header: ['mm (KOR)', '250', '255', '260', '265', '270', '275', '280', '285', '290', '295', '300'],
  rows: [['ZA 2020', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46', '47'], ['ZA 2021', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46', '47'], ['ZA 2024', '38', '39', '40', '41', '42', '43', '44', '45', '46', '', ''], ['ZA 3010', '38', '39', '40', '41', '42', '43', '44', '45', '46', '', ''], ['ZA 3020', '38', '39', '40', '41', '42', '43', '44', '45', '46', '', ''], ['PRO 15944', 'M (39-40)', 'M (39-40)', 'L (41)', 'L (41)', 'XL (42-43)', 'XL (42-43)', 'XXL (44)', 'XXL (44)', 'XXXL (45-46)', 'XXXL (45-46)', 'XXXXL (47-48)'], ['PRITF 2022', 'XS', 'XS', 'XS', 'XS', 'S', 'S', 'M', 'M', 'L', 'L', 'XL']]
};
/**
 * Bottom tabbed information panel for the Gen3 E-Foot Gear page.
 * Owns its own activeTab state. Listens for a window event from
 * PurchaseCard so the "View sizing chart" link there can flip this
 * section to the sizing tab without prop drilling.
 */
export function Tabs() {
  const [activeTab, setActiveTab] = useState<TabId>('description');
  useEffect(() => {
    const handler = () => setActiveTab('sizing');
    window.addEventListener(VIEW_SIZING_CHART_EVENT, handler);
    return () => window.removeEventListener(VIEW_SIZING_CHART_EVENT, handler);
  }, []);
  return <div className="mt-16" data-id="element-512">
      <div className="border-b border-gray-200" data-id="element-513">
        <div className="flex gap-1 overflow-x-auto -mb-px" data-id="element-514">
          <TabButton active={activeTab === 'description'} onClick={() => setActiveTab('description')} data-id="element-515">
            Description
          </TabButton>
          <TabButton active={activeTab === 'sizing'} onClick={() => setActiveTab('sizing')} data-id="element-516">
            Size Chart
          </TabButton>
          <TabButton active={activeTab === 'related'} onClick={() => setActiveTab('related')} data-id="element-517">
            Related Products
          </TabButton>
          <TabButton active={activeTab === 'shipping'} onClick={() => setActiveTab('shipping')} data-id="element-518">
            Shipping / Returns
          </TabButton>
        </div>
      </div>

      <div className="bg-white rounded-b-2xl border border-t-0 border-gray-100 p-6 md:p-10" data-id="element-519">
        {activeTab === 'description' && <DescriptionPanel data-id="element-520" />}
        {activeTab === 'sizing' && <SizingPanel data-id="element-521" />}
        {activeTab === 'related' && <RelatedPanel data-id="element-522" />}
        {activeTab === 'shipping' && <ShippingPanel data-id="element-523" />}
      </div>
    </div>;
}
/* ---- Local helpers (private to this section) ---- */
function TabButton({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return <button type="button" onClick={onClick} className={`px-5 py-3 text-sm font-bold whitespace-nowrap border-b-2 transition-colors ${active ? 'border-red-600 text-red-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-524">
      {children}
    </button>;
}
function DescriptionPanel() {
  return <div className="prose max-w-none" data-id="element-525">
      <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-526">
        About the Gen3 E-Foot Gear
      </h3>
      <p className="text-gray-700 leading-relaxed mb-4" data-id="element-527">
        The Daedo Gen3 E-Foot Gear is the official sensor sock for Gen3
        electronic scoring. Designed for competition-level accuracy, the Gen3
        sock features additional sensors and improved detection for use with
        Gen3 E-Hogus and E-Headgear.
      </p>
      <p className="text-gray-700 leading-relaxed mb-4" data-id="element-528">
        Built for elite athletes and serious training environments, the Gen3
        E-Foot Gear provides robust, reliable scoring detection across practice
        and tournament play.
      </p>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-6" data-id="element-529">
        <p className="text-sm text-amber-900" data-id="element-530">
          <span className="font-bold" data-id="element-531">Important:</span> Gen3 footgear is only
          compatible with Gen3 Electronic Protectors (E-Hogus). For Gen2
          systems, please use Gen2 footgear.
        </p>
      </div>
    </div>;
}
function SizingPanel() {
  return <div data-id="element-532">
      <div className="text-center mb-6" data-id="element-533">
        <h3 className="text-xl md:text-2xl font-black text-gray-900 uppercase tracking-tight" data-id="element-534">
          Don't Know Your Foot Size?
        </h3>
        <p className="text-base font-semibold text-gray-600 mt-1" data-id="element-535">
          E-Footgear Suggested Sizing Chart
        </p>
      </div>

      <SizingTable data={SIZING_TOP} data-id="element-536" />
      <div className="h-4" data-id="element-537" />
      <SizingTable data={SIZING_BOTTOM} data-id="element-538" />

      <div className="text-center text-sm text-gray-600 max-w-2xl mx-auto mt-6 bg-gray-50 rounded-xl p-4" data-id="element-539">
        <p data-id="element-540">
          <span className="font-semibold text-gray-900" data-id="element-541">
            This chart is a guideline.
          </span>{' '}
          Width, thickness and length of your foot should all be considered when
          purchasing e-footgear.
        </p>
      </div>
    </div>;
}
function SizingTable({
  data
}: {
  data: {
    header: string[];
    rows: string[][];
  };
}) {
  return <div className="overflow-x-auto rounded-xl border border-gray-200" data-id="element-542">
      <table className="min-w-full text-center text-xs sm:text-sm" data-id="element-543">
        <thead data-id="element-544">
          <tr className="bg-blue-50 text-blue-800" data-id="element-545">
            {data.header.map((h, i) => <th key={i} className="px-3 py-2.5 font-bold border-b border-blue-100 whitespace-nowrap" data-id="element-546">
                {h}
              </th>)}
          </tr>
        </thead>
        <tbody data-id="element-547">
          {data.rows.map((row, i) => <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'} data-id="element-548">
              {row.map((cell, j) => <td key={j} className={`px-3 py-2.5 whitespace-nowrap ${j === 0 ? 'font-semibold text-blue-700 text-left' : 'text-gray-700'}`} data-id="element-549">
                  {cell || '—'}
                </td>)}
            </tr>)}
        </tbody>
      </table>
    </div>;
}
function RelatedPanel() {
  return <div data-id="element-550">
      <h3 className="text-xl font-bold text-gray-900 mb-1" data-id="element-551">
        Other Suggested TrueScore Products
      </h3>
      <p className="text-sm text-gray-600 mb-6" data-id="element-552">
        Complete your Gen3 setup with these recommended pieces.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-553">
        {RELATED.map((product, idx) => <a key={idx} href={product.href} className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-gray-300 transition-all" data-id="element-554">
            <div className="aspect-square bg-gray-50 flex items-center justify-center p-6 overflow-hidden" data-id="element-555">
              <img src={product.image} alt={product.name} className="block max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-105" data-id="element-556" />
            </div>
            <div className="p-4" data-id="element-557">
              <h4 className="font-bold text-gray-900 mb-1 text-base" data-id="element-558">
                {product.name}
              </h4>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-red-600 group-hover:text-red-700" data-id="element-559">
                View Product
                <ChevronRightIcon className="w-4 h-4" data-id="element-560" />
              </span>
            </div>
          </a>)}
      </div>
    </div>;
}
function ShippingPanel() {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-561">
      <div data-id="element-562">
        <div className="flex items-center gap-3 mb-3" data-id="element-563">
          <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-564">
            <TruckIcon className="w-5 h-5 text-blue-700" data-id="element-565" />
          </div>
          <h3 className="text-lg font-bold text-gray-900" data-id="element-566">Shipping</h3>
        </div>
        <p className="text-sm text-gray-700 leading-relaxed" data-id="element-567">
          We ship competition gear across the U.S. Most orders are processed
          within 1-2 business days. Tracking is provided once your order ships.
        </p>
      </div>
      <div data-id="element-568">
        <div className="flex items-center gap-3 mb-3" data-id="element-569">
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center" data-id="element-570">
            <ShieldCheckIcon className="w-5 h-5 text-red-600" data-id="element-571" />
          </div>
          <h3 className="text-lg font-bold text-gray-900" data-id="element-572">Returns</h3>
        </div>
        <p className="text-sm text-gray-700 leading-relaxed" data-id="element-573">
          Unused, unopened gear may be returned within our standard return
          window.{' '}
          <span className="font-semibold text-red-600" data-id="element-574">
            Lasered/personalized items are final sale.
          </span>{' '}
          See our full{' '}
          <a href="/return-policy" className="text-blue-600 underline" data-id="element-575">
            return policy
          </a>{' '}
          for details.
        </p>
      </div>
    </div>;
}